/**
 * Drive client for Connect — the Teams model: a file sent in a chat
 * (or a meeting recording) lives in the SENDER's Drive, in a special folder,
 * and is shared with the people in the conversation. Connect never stores the
 * bytes itself.
 *
 * Connect's own access token works against Drive as-is: Identity puts the
 * Drive audience in every token and Drive's CORS allows meeting.fowlguard.com.
 *
 * Every URL here is built from a Drive item id — never from anything stored
 * in a message (names, types and owners in a message are display hints only).
 */

const driveBase = (import.meta.env.VITE_DRIVE_API_URL ?? "https://drive.fowlguard.com").replace(/\/+$/, "");
const driveWeb = (import.meta.env.VITE_DRIVE_WEB_URL ?? "https://drive.fowlguard.com").replace(/\/+$/, "");
const docsWeb = (import.meta.env.VITE_DOCS_WEB_URL ?? "https://notes.fowlguard.com").replace(/\/+$/, "");
const sheetsWeb = (import.meta.env.VITE_SHEET_WEB_URL ?? "https://workbook.fowlguard.com").replace(/\/+$/, "");
const slidesWeb = (import.meta.env.VITE_PRESENT_WEB_URL ?? "https://present.fowlguard.com").replace(/\/+$/, "");

/** Drive's 1 GB per-file limit — checked client-side so a huge file fails fast. */
export const MAX_DRIVE_FILE_BYTES = 1024 * 1024 * 1024;

export interface DriveItem {
    id: string;
    parentId: string | null;
    kind: string;
    name: string;
    contentType: string | null;
    sizeBytes: number;
    createdAt: string;
    updatedAt: string;
    /** Set only when someone else owns the item and shared it with you. */
    ownerEmail?: string;
    role?: "viewer" | "editor";
    /** False for a view-only share with download blocked. Absent = you own it. */
    canDownload?: boolean;
}

export interface DriveSharing {
    itemId: string;
    ownerEmail: string;
    myRole: string;
    canManage: boolean;
    people: Array<{ id: string; email: string; role: string; allowDownload: boolean; grantedBy: string; createdAt: string; source: string | null }>;
}

export type DriveShareSource = "connect-chat" | "connect-channel" | "connect-recording";

/** A Drive request failed; `message` is the server's ProblemDetails title/detail when it sent one. */
export class DriveError extends Error {
    constructor(message: string, readonly status: number) {
        super(message);
        this.name = "DriveError";
    }
}

/** The item is gone or the caller no longer has access (Drive answers 404 for both). */
export class DriveNotFoundError extends DriveError {
    constructor() {
        super("File unavailable", 404);
        this.name = "DriveNotFoundError";
    }
}

/** The Drive item ids Connect hands out are GUIDs; anything else is refused before it reaches a URL. */
export function isDriveId(id: string | null | undefined): id is string {
    return typeof id === "string" && /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/.test(id);
}

function url(path: string): string {
    return `${driveBase}${path}`;
}

async function problemMessage(res: Response, fallback: string): Promise<string> {
    try {
        const body = (await res.json()) as { title?: string; detail?: string };
        const parts = [body.title, body.detail].filter((s): s is string => Boolean(s && s.trim()));
        if (parts.length > 0) return parts.join(" — ");
    } catch { /* not JSON */ }
    return fallback;
}

async function driveJson<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body && typeof init.body === "string") headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    let res: Response;
    try {
        res = await fetch(url(path), { ...init, headers, credentials: "omit" });
    } catch {
        throw new DriveError("Couldn't reach Drive. Check your connection and try again.", 0);
    }
    if (res.status === 404) throw new DriveNotFoundError();
    if (!res.ok) throw new DriveError(await problemMessage(res, `Drive returned ${res.status}`), res.status);
    return (await res.json()) as T;
}

/** Get-or-create the caller's "Connect Chat Files" (chat) or "Recordings" folder. */
export function ensureSpecialFolder(kind: "chat" | "recordings", token: string | undefined): Promise<DriveItem> {
    return driveJson<DriveItem>(`/api/special-folders/${kind}`, token, { method: "POST" });
}

export function getDriveItem(id: string, token: string | undefined): Promise<DriveItem> {
    if (!isDriveId(id)) return Promise.reject(new DriveNotFoundError());
    return driveJson<DriveItem>(`/api/items/${encodeURIComponent(id)}`, token);
}

/**
 * Give people access to an item. `keepHigherRole` is always on: re-sending a
 * file to another chat must never downgrade someone who can already edit it.
 */
export function grantDriveAccess(itemId: string, grant: {
    emails: string[];
    role: "viewer" | "editor";
    allowDownload: boolean;
    source: DriveShareSource;
    sourceRef: string;
}, token: string | undefined): Promise<DriveSharing> {
    return driveJson<DriveSharing>(`/api/items/${encodeURIComponent(itemId)}/permissions`, token, {
        method: "POST",
        body: JSON.stringify({ ...grant, keepHigherRole: true })
    });
}

/**
 * A short-lived (5 min) anonymous URL for one file. "download" streams it as
 * an attachment (a native browser download); "preview" is allowed even for
 * view-only people and is meant for <img>/<video>/<audio> src.
 */
export async function createDownloadLink(id: string, purpose: "download" | "preview", token: string | undefined): Promise<string> {
    if (!isDriveId(id)) throw new DriveNotFoundError();
    const res = await driveJson<{ url: string; expiresAt: string }>("/api/download-links", token, {
        method: "POST",
        body: JSON.stringify({ itemIds: [id], purpose })
    });
    // The server returns a relative "/api/dl/<token>"; refuse anything that
    // would point somewhere other than Drive.
    if (!res.url.startsWith("/api/dl/")) throw new DriveError("Drive returned an unexpected link", 500);
    return url(res.url);
}

/** The image thumbnail as a Blob, or null when the file has none. */
export async function fetchThumbnail(id: string, token: string | undefined, signal?: AbortSignal): Promise<Blob | null> {
    if (!isDriveId(id)) return null;
    const res = await fetch(url(`/api/files/${encodeURIComponent(id)}/thumbnail`), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "omit",
        signal
    });
    if (!res.ok) return null;
    return res.blob();
}

/** Start a real browser download of one file (the link is an attachment, so the page stays put). */
export async function downloadDriveFile(id: string, token: string | undefined): Promise<void> {
    const href = await createDownloadLink(id, "download", token);
    const a = document.createElement("a");
    a.href = href;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
}

export interface UploadHandle {
    promise: Promise<DriveItem>;
    abort: () => void;
}

/**
 * Upload one file into a folder with progress and cancel. XMLHttpRequest
 * rather than fetch because fetch still can't report upload progress.
 * Drive renames on a name collision ("x (2).ext").
 */
export function uploadToDrive(file: File, parentId: string, token: string | undefined, onProgress?: (fraction: number) => void): UploadHandle {
    const xhr = new XMLHttpRequest();
    const promise = new Promise<DriveItem>((resolve, reject) => {
        xhr.open("POST", url("/api/files"));
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.setRequestHeader("Accept", "application/json");
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && e.total > 0) onProgress?.(Math.min(1, e.loaded / e.total));
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    onProgress?.(1);
                    resolve(JSON.parse(xhr.responseText) as DriveItem);
                } catch {
                    reject(new DriveError("Drive sent an unreadable reply", xhr.status));
                }
                return;
            }
            let message = xhr.status === 413 ? "The file is too large for Drive." : `Upload failed (${xhr.status})`;
            try {
                const body = JSON.parse(xhr.responseText) as { title?: string; detail?: string };
                const parts = [body.title, body.detail].filter((s): s is string => Boolean(s && s.trim()));
                if (parts.length > 0) message = parts.join(" — ");
            } catch { /* keep the generic message */ }
            reject(new DriveError(message, xhr.status));
        };
        xhr.onerror = () => reject(new DriveError("Upload failed — check your connection.", 0));
        xhr.onabort = () => reject(new DriveError("Upload cancelled", -1));
        const form = new FormData();
        form.append("file", file, file.name);
        form.append("parentId", parentId);
        xhr.send(form);
    });
    return { promise, abort: () => xhr.abort() };
}

// ── File kinds + links ──────────────────────────────────────────────────────

export type FileKind = "image" | "video" | "audio" | "pdf" | "word" | "excel" | "powerpoint" | "archive" | "text" | "file";

export function fileKind(name: string, contentType?: string | null): FileKind {
    const ct = (contentType ?? "").toLowerCase();
    const ext = name.toLowerCase().split(".").pop() ?? "";
    if (["doc", "docx", "odt", "rtf"].includes(ext)) return "word";
    if (["xls", "xlsx", "ods", "csv"].includes(ext)) return "excel";
    if (["ppt", "pptx", "odp"].includes(ext)) return "powerpoint";
    if (ct === "application/pdf" || ext === "pdf") return "pdf";
    // SVG is left out on purpose: it can carry script, so it's never rendered inline.
    if ((ct.startsWith("image/") && ct !== "image/svg+xml") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "avif"].includes(ext)) return "image";
    if (ct.startsWith("video/") || ["mp4", "webm", "mov", "m4v", "ogv"].includes(ext)) return "video";
    if (ct.startsWith("audio/") || ["mp3", "wav", "ogg", "oga", "flac", "m4a", "aac", "opus"].includes(ext)) return "audio";
    if (["zip", "rar", "7z", "tar", "gz", "tgz"].includes(ext)) return "archive";
    if (ct.startsWith("text/") || ["txt", "md", "log", "json", "xml"].includes(ext)) return "text";
    return "file";
}

/** Documents, workbooks and presentations open in Notes / Sheets / Slides with the recipient's own sign-in. */
export function editorUrl(id: string, name: string, kind: FileKind = fileKind(name)): string | null {
    if (!isDriveId(id)) return null;
    const q = `fileId=${encodeURIComponent(id)}&fileName=${encodeURIComponent(name)}`;
    switch (kind) {
        case "word": return `${docsWeb}/edit?${q}`;
        case "excel": return `${sheetsWeb}/?${q}`;
        case "powerpoint": return `${slidesWeb}/?${q}`;
        default: return null;
    }
}

export const driveWebUrl = `${driveWeb}/`;

/**
 * Drive's universal link for one item (`/open/<id>`): it opens the file in
 * Drive for anyone it's shared with, or Drive's home when the id isn't valid.
 */
export function driveOpenUrl(id: string): string {
    return isDriveId(id) ? `${driveWeb}/open/${encodeURIComponent(id)}` : driveWebUrl;
}

export function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}

export function formatDuration(seconds: number): string {
    const s = Math.max(0, Math.round(seconds));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
        ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
        : `${m}:${String(sec).padStart(2, "0")}`;
}
